import { numberToUserDisplay, userEntryToNumber } from '@ebiz/pretty-num';
import type { ZoomTick, ZoomRect } from './zoom';

export default class XZoomBound {
  isClosing: boolean;
  zoomMin: number;
  zoomMax: number;
  dim: 'x' | 'y' = 'x';
  direction = -1;
  displayType = 'Engineering';
  enclosingTicks;

  constructor(zoomSettings: ZoomRect, ticksWithLimits: ZoomTick[], isClosing: boolean) {
    this.isClosing = isClosing;
    // These lines specialize X vs Y
    this.zoomMin = zoomSettings.x;
    this.zoomMax = zoomSettings.x + zoomSettings.width;
    // Cache the enclosing ticks calculation rather than using a getter
    this.enclosingTicks = this.getEnclosingTicks(ticksWithLimits);
  }
  // The following subtly differ from the corresponding methods in x-zoom-bound
  getEnclosingTicks(ticks: ZoomTick[]) {
    let previous = null;
    for (let i = 0; i < ticks.length; i++) {
      const tick = ticks[i] as ZoomTick; // Can't be undefined but TS doesn't see it
      if ((this.isClosing && tick[this.dim] >= this.zoomMax) || (!this.isClosing && tick[this.dim] > this.zoomMin)) {
        return { index: i, previousTick: previous, nextTick: tick };
      }
      previous = tick;
    }
    return { index: ticks.length, previousTick: previous, nextTick: null };
  }

  // Everything below this point is precisely common across zoom bound types
  get openingNumericValue() {
    if (this.enclosingTicks.previousTick && this.enclosingTicks.nextTick) {
      // Interpolate between previous and next tick
      const previousValue = userEntryToNumber(this.enclosingTicks.previousTick.value);
      const nextValue = userEntryToNumber(this.enclosingTicks.nextTick.value);
      const coveredArea = this.enclosingTicks.nextTick[this.dim] - this.zoomMin;
      const pxBetweenTicks = this.enclosingTicks.nextTick[this.dim] - this.enclosingTicks.previousTick[this.dim];
      return nextValue + (this.direction * Math.abs(nextValue - previousValue) * coveredArea) / pxBetweenTicks;
    } else if (this.enclosingTicks.nextTick) {
      // Point is off start of graph - No interpolation needed
      return userEntryToNumber(this.enclosingTicks.nextTick.value);
    } else if (this.enclosingTicks.previousTick) {
      // Point is off end of graph - No interpolation needed
      return userEntryToNumber(this.enclosingTicks.previousTick.value);
    } else {
      return null;
    }
  }
  get closingNumericValue() {
    if (this.enclosingTicks.previousTick && this.enclosingTicks.nextTick) {
      // Interpolate between previous and next tick
      const previousValue = userEntryToNumber(this.enclosingTicks.previousTick.value);
      const nextValue = userEntryToNumber(this.enclosingTicks.nextTick.value);
      const valueDiff = Math.abs(nextValue - previousValue);
      const coveredArea = this.zoomMax - this.enclosingTicks.previousTick[this.dim];
      const pxBetweenTicks = this.enclosingTicks.nextTick[this.dim] - this.enclosingTicks.previousTick[this.dim];
      return previousValue - (this.direction * valueDiff * coveredArea) / pxBetweenTicks;
    } else if (this.enclosingTicks.nextTick) {
      // Point is off start of graph - No interpolation needed
      return userEntryToNumber(this.enclosingTicks.nextTick.value);
    } else if (this.enclosingTicks.previousTick) {
      // Point is off end of graph - No interpolation needed
      return userEntryToNumber(this.enclosingTicks.previousTick.value);
    } else {
      return null;
    }
  }
  get numericValue() {
    return this.isClosing ? this.closingNumericValue : this.openingNumericValue;
  }
  get displayValue() {
    return this.numericValue !== null
      ? numberToUserDisplay(this.numericValue, this.displayType, 5, { showZeroes: false })
      : '';
  }
}
